import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import StaticPage from "../../model/StaticPage";
import FormRichTextEditor from "../common/FormRichTextEditor";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormContainer from "../common/FormContainer";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";

interface IStaticPageFormErrors {
  title: string;
  slug: string;
  content: string;
}

const defaultFormErrors: IStaticPageFormErrors = {
  title: "",
  slug: "",
  content: "",
};

/*
  Component to manage the display of the existing SITES list
*/
function StaticPageAddEdit() {
  // read the ID from the url
  const { id } = useParams();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // reference to the navigator
  const apiClient = useApiClient();
  // manage data loading animation
  const [loading, setLoading] = useState(true);
  // keep the data for the current static page
  const [formData, setFormData] = useState<StaticPage>(
    StaticPage.EmptyInstance(),
  );
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IStaticPageFormErrors>(defaultFormErrors);
  // get a reference to navigation component
  const navigate = useNavigate();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let pageInfo: StaticPage | undefined = undefined;
        if (id) {
          pageInfo = await apiClient?.get<StaticPage>({
            url: APIEndPoints.STATIC_PAGES.ONE(parseInt(id)),
          });
          setFormData(pageInfo);
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }

      setTimeout(() => {
        console.log("info = ", formData);
        setLoading(false);
      }, 200);
    };

    getAllData();
  }, [apiClient, id]);

  const formInputChange = (inputID: string, newValue: string) => {
    console.log("formInputChange CALLED - " + inputID);
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as StaticPage);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // get the errors from the form
    const errors = validateStaticPageForm(formData);

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    if (id) {
      // UPDATE
      apiClient
        ?.put<StaticPage>({
          url: APIEndPoints.STATIC_PAGES.ONE(parseInt(id!)),
          requiresToken: true,
          body: formData,
        })
        .then((response: StaticPage) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Record updated successfully",
          });
          navigate(SETTINGS.go_back({ section: AppSections.STATIC_PAGES }));
        })
        .catch(() => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Failed to update the record",
          });
        });
    } else {
      // CREATE
      apiClient
        ?.post<StaticPage>({
          url: APIEndPoints.STATIC_PAGES.NEW,
          requiresToken: true,
          body: formData,
        })
        .then((response: StaticPage) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Record created successfully",
          });
          navigate(SETTINGS.go_back({ section: AppSections.STATIC_PAGES }));
        })
        .catch(() => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Failed to update the record",
          });
        });
    }
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.STATIC_PAGES }));
  };

  const pageTitle = id ? "Update static page" : "Create static page";
  const confirmLabel = id ? "Update record" : "Create record";

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      css=""
    >
      <div className="flex flex-col h-full">
        <FormInputText
          label="Page title"
          id="title"
          value={formData.title}
          required={true}
          placeholder="Title"
          onChange={formInputChange}
          error={formErrors.title}
        />

        <div className="mt-5">
          <FormInputText
            label="Page identifier"
            id="slug"
            value={formData.slug}
            required={true}
            placeholder="Unique page identifier as string"
            onChange={formInputChange}
            error={formErrors.slug}
          />
        </div>

        <FormRichTextEditor
          id="content"
          value={formData.content}
          required={false}
          onChange={formInputChange}
          className="quill-editor h-full mt-10"
        />

        {errorMessage && (
          <div className="form-submit-error">
            <FaInfoCircle />
            <div>{errorMessage}</div>
          </div>
        )}

        <div className="form-buttons-container">
          <Button
            label="Cancel"
            purpose={ButtonAction.CANCEL}
            onClick={() => onFormCancel()}
            className="w-full"
          />
          <Button
            label={confirmLabel}
            purpose={ButtonAction.SAVE}
            isSubmit={true}
            className="w-full"
          />
        </div>
      </div>
    </FormContainer>
  );
}

function validateStaticPageForm(formData: StaticPage): IStaticPageFormErrors {
  let errors: IStaticPageFormErrors = { title: "", content: "", slug: "" };
  errors.title =
    formData.title.length < 4
      ? "Page title should be at least 4 characters long"
      : "";
  errors.slug =
    formData.slug.length < 4
      ? "Page identifier should be at least 4 characters long"
      : "";
  errors.content =
    formData.content.length < 10
      ? "Page content should be at least 10 characters long"
      : "";

  return errors;
}

function isFormInvalid(errors: IStaticPageFormErrors): boolean {
  return errors.title !== "" || errors.content !== "" || errors.slug !== "";
}

export default StaticPageAddEdit;
